const PLACEHOLDER_FIRST_NAMES = new Set(['friend', 'user', 'guest']);

/** Name from `users` row — empty when missing or placeholder-only. */
export function userAccountDisplayName(user) {
  const first = String(user?.firstName || '').trim();
  const last = String(user?.lastName || '').trim();
  if (!first && !last) return '';
  if (PLACEHOLDER_FIRST_NAMES.has(first.toLowerCase()) && !last) return '';
  return [first, last].filter(Boolean).join(' ').trim();
}

/** Participant name on a booking row (traveler or linked registration). */
export function bookingParticipantDisplayName(booking) {
  if (!booking) return '';
  const t0 = Array.isArray(booking.travelers) ? booking.travelers[0] : null;
  const reg = booking.registration;
  return [t0?.firstName || reg?.firstName, t0?.lastName || reg?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
}

function participantRowFromTraveler(traveler, index) {
  const firstName = String(traveler?.firstName || '').trim();
  const lastName = String(traveler?.lastName || '').trim();
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (!name) return null;
  return { index: index + 1, firstName, lastName, name };
}

/** Indexed list for booking confirmed / invoices — uses API groupParticipants when present. */
export function resolveIndexedBookingParticipants(booking) {
  const fromApi = Array.isArray(booking?.groupParticipants) ? booking.groupParticipants : [];
  if (fromApi.length) {
    return fromApi
      .map((row, i) => {
        const name = String(row?.name || '').trim()
          || [row?.firstName, row?.lastName].filter(Boolean).join(' ').trim();
        if (!name) return null;
        return {
          index: Number(row?.index) > 0 ? Number(row.index) : i + 1,
          name
        };
      })
      .filter(Boolean);
  }

  // Fallback: Razorpay group receipt notes ("Participants: A, B, C")
  const receipts = Array.isArray(booking?.receipts) ? booking.receipts : [];
  for (const receipt of receipts) {
    const match = String(receipt?.notes || '').match(/Participants:\s*([^|]+)/i);
    if (!match) continue;
    const names = match[1]
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean);
    if (names.length > 1) {
      return names.map((name, i) => ({ index: i + 1, name }));
    }
  }

  const travelers = Array.isArray(booking?.travelers) ? booking.travelers : [];
  if (travelers.length > 1) {
    return travelers.map((t, i) => participantRowFromTraveler(t, i)).filter(Boolean);
  }

  const single = bookingParticipantDisplayName(booking);
  return single ? [{ index: 1, name: single }] : [];
}

/** Greeting / header: account name, else latest booking participant, else fallback. */
export function resolveCustomerDisplayName(user, bookings, fallback = 'Adventurer') {
  const fromAccount = userAccountDisplayName(user);
  if (fromAccount) return fromAccount;
  const list = Array.isArray(bookings) ? bookings : [];
  for (const booking of list) {
    const name = bookingParticipantDisplayName(booking);
    if (name) return name;
  }
  return fallback;
}
